import {useLearningStore} from "~/store/learning/learningStore";
import {useLearningAssessmentStore} from "~/store/learning/assessment/learningAssessmentStore";
import {RecorderDomainType} from "~~/package/mapper/recorder/model/RecorderDomainType";
import {useDialogStore} from "~/store/dialog/dialogStore";
import DialogType from "~/components/Common/Dialog/model/DialogType";
import {isDesktop} from "~~/package/utils/OSUtil";
import {useI18n, useContainer} from "#imports";
import {applicationContext} from "@@/package/application/applicationContext";
import { useGenericDialog } from "~/composables/useGenericDialog";
import { convertToNextLine } from "~/utils/StringUtil";
import { DependencyInjectId } from "~~/package/const/DependencyInjectId";
import type UserApi from "~~/package/apis/user/UserApi";

const DEVICE_ERROR_NAMES = new Set([
  'CamStreamEnded', 'MonitorStreamEnded',
  'NotAllowedError', 'NotFoundError', 'NotReadableError',
  'GetUserMediaFailed', 'OverconstrainedError', 'NoVideoTrack',
]);

// Proctive webview-preload의 onRecordingError는 부를 때마다 ipcRenderer 리스너를 새로 붙이고 해제 수단이 없다.
// 응시 화면은 문항마다 다시 마운트되며 이 핸들러를 다시 등록하므로, 리스너가 쌓이면 녹화 에러 한 번에
// close-info 전송·실패 팝업·설정 페이지 이동이 지나온 문항 수만큼 반복됐다. 브리지에는 문서당 한 번만 붙이고
// 지금 화면의 구독자 하나에만 전달한다.
let recordingErrorSubscriber: ((data: ProctiveRecordingError) => void) | null = null;
let recordingErrorBridgeBound = false;

export const useExternalDirectorService = (domainType = RecorderDomainType.NONE) => {
  const config = { public: applicationContext.getConfig() }
  const learningStore = useLearningStore();
    const assessmentStore = useLearningAssessmentStore();
  const dialogStore = useDialogStore();
  const { t } = useI18n();
  const { openGenericAlert } = useGenericDialog();
  const userApi = useContainer<UserApi>(DependencyInjectId.UserApi);
  const isProcessing = ref(false);


  const getVersion = () => {
    return window.codepresso?.version ?? '';
  }

  /**
   * TSK-11690 / REQ-313 — Proctive의 응시 환경 점검 브리지.
   *
   * 기존에는 이 페이지가 camera/microphone/screen 콜백을 모아 직접 판정했다. 그 판정은
   * "장치가 목록에 있고 권한이 있다"까지만 봤기 때문에, 점검을 통과하고도 응시 시작 직후 녹화가
   * 비는 사고가 났다. 이제 Proctive가 실제 녹화 리허설(프레임·인코더 출력·HLS 세그먼트)로 판정하고
   * 여기서는 결과만 받아 표시한다.
   *
   * 계약 정본: proctive `docs/req-313-env-check-rehearsal.md` §6
   * 구버전 Proctive/일반 브라우저에서는 undefined다 — 호출부는 반드시 존재 여부를 확인하고
   * 없으면 기존 브리지 판정 로직을 그대로 유지해야 한다.
   */
  const getEnvCheckBridge = () => (window as any).codepresso?.envCheck ?? null;

  const checkVersion = () => {
    // Codepresso Webview 환경이 아니면 체크하지 않음
    if (!isCodepressoWebview.value) {
      return;
    }

    const currentVersion = getVersion();
    const requiredVersion = config.public.DESKTOP_APP_VERSION;

    // 1차 QA: 버전이 다르면 팝업만이 아니라 Proctive 타이틀바에도 "업데이트 필요"가 보여야 한다.
    // 런처는 자기 autoUpdater 상태만 알고 플랫폼의 필수 버전은 모르므로 여기서 알려준다.
    // (일치하는 경우에도 보내야 런처가 "뒤처지지 않았다"를 확정할 수 있다.)
    window.codepresso?.sendToHost?.('requiredAppVersion', { requiredVersion });

    // 버전이 있고 필수 버전과 일치하지 않으면 업데이트 팝업 표시
    if (currentVersion !== requiredVersion) {
      showDesktopAppInstallPopup(true);
    }
  }

  const isCodepressoWebview = computed(() => {
    return window.codepresso?.isCodepressoWebView ?? false;
  })

  const isMac = computed(() => {
    return window.codepresso?.isMac ?? false;
  })

  // 이전 입력 디바이스 목록 스냅샷 (videoinput + audioinput) — 모니터만 변경 시 skip용
  let prevInputDeviceIds: Set<string> = new Set();

  const initDeviceSnapshot = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    prevInputDeviceIds = new Set(
      devices
        .filter(d => d.kind === 'videoinput' || d.kind === 'audioinput')
        .map(d => d.deviceId)
    );
  }

  const devicechangeHandler = async (callback: () => void) => {
    if(assessmentStore.externalDirector.camId && assessmentStore.externalDirector.micId) {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const currentInputDeviceIds = new Set(
        devices
          .filter(d => d.kind === 'videoinput' || d.kind === 'audioinput')
          .map(d => d.deviceId)
      );

      // 입력 디바이스(cam/mic)에 변화가 없으면 skip (모니터 연결 등)
      const inputDevicesChanged =
        prevInputDeviceIds.size !== currentInputDeviceIds.size ||
        [...prevInputDeviceIds].some(id => !currentInputDeviceIds.has(id));

      prevInputDeviceIds = currentInputDeviceIds;

      if (!inputDevicesChanged) return;

      const stillHasCam = devices.some(d => d.kind === 'videoinput' && d.deviceId === assessmentStore.externalDirector.camId);
      const stillHasMic = devices.some(d => d.kind === 'audioinput'  && d.deviceId === assessmentStore.externalDirector.micId);

      if(window.codepresso && (!stillHasCam || !stillHasMic)) {
        setTimeout(() => {
          openGenericAlert({
            title: t('skillCertify-proctoring-face-voice-recording-set-up-fail-title'),
            contents: convertToNextLine(t('skillCertify-proctoring-face-voice-recording-set-up-fail-description')),
            icon: 'micCamFail',
            popupClass: 'dark assessment',
          });

        }, 100);
        window.codepresso!.sendToHost('disconnectDevice', {});
        callback();
      }
    }
  }

  /** 녹화 에러를 이 화면에서 받는다. 돌려주는 함수로 구독을 해제한다(이미 다른 화면이 넘겨받았으면 아무것도 안 한다). */
  const recordingErrorHandler = (callback?: () => void): (() => void) => {
    if (!window.codepresso?.onRecordingError) return () => {};
    const subscriber = (data: ProctiveRecordingError) => {
      setTimeout(() => {
        if (DEVICE_ERROR_NAMES.has(data?.name ?? '')) {
          if (data?.videoType === 'monitor') {
            dialogStore.open({type: DialogType.RecordingFullScreenPopup});
          } else {
            openGenericAlert({
              title: t('skillCertify-proctoring-face-voice-recording-set-up-fail-title'),
              contents: convertToNextLine(t('skillCertify-proctoring-face-voice-recording-set-up-fail-description')),
              icon: 'micCamFail',
              popupClass: 'dark assessment',
            });
          }
        } else {
          dialogStore.open({type: DialogType.ProctiveRecordingFailPopup});
        }
      }, 100);

      console.error('[taskId] 녹화 에러 발생:', data);
      // 설정 페이지로 이동
      if(callback) callback();
    };
    recordingErrorSubscriber = subscriber;
    if (!recordingErrorBridgeBound) {
      recordingErrorBridgeBound = true;
      window.codepresso.onRecordingError((data) => recordingErrorSubscriber?.(data));
    }
    return () => {
      if (recordingErrorSubscriber === subscriber) recordingErrorSubscriber = null;
    };
  }

  const startCertify = (data: {
    id: number,
    title: string,
    startedAt: number,
    endedAt: number,
    camId: string,
    micId: string,
    organizationName: string
  }) => {
    if(window.codepresso) window.codepresso!.sendToHost('startCertify', data)
  }

  const finishCertify = () => {
    if(window.codepresso) window.codepresso!.sendToHost('finishCertify', {});
  }

  const updateTimer = (stringifiedTimer: string) => {
    if(window.codepresso) {
      window.codepresso.sendToHost('updateTimer', {stringifiedTimer});
    }
  }

  // ASMT-PROCTOR-11: Proctive 데스크톱 앱 deeplink 실행, 미설치/실행 실패 시 설치 팝업(진행 차단)
  const openExternalDirectorApp = async () => {
    // 이미 처리 중이면 무시
    if (isProcessing.value) {
      return;
    }

    isProcessing.value = true;

    // SEC-1 (TSK-10568): JS can't read the HttpOnly AccessToken cookie. Mint a 60-second
    // single-purpose token via /user/deeplink-token instead, and pass it to the Proctive app.
    let shortToken: string;
    try {
      const response = await userApi.getDeeplinkToken();
      shortToken = response.data.data;
    } catch (e) {
      isProcessing.value = false;
      console.error("Failed to mint Proctive deeplink token", e);
      return;
    }

    const deeplink = `proctive://open?url=${encodeURIComponent(window.location.href)}&token=${shortToken}`;
    // 데스크톱이 아닌 경우 바로 실행
    if (!isDesktop()) {
      window.location.href = deeplink;
      return;
    }

    // 데스크톱인 경우 앱 설치 확인 후 실행
    let appLaunched = false;
    let timeoutId: NodeJS.Timeout;
    
    // 앱이 실행되었는지 확인하는 함수
    const checkAppLaunched = () => {
      appLaunched = true;
      clearTimeout(timeoutId);
      isProcessing.value = false;
      // 이벤트 리스너 제거
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onWindowFocus);
    };
    
    // visibility change 이벤트 핸들러
    const onVisibilityChange = () => {
      if (document.hidden) {
        // 페이지가 숨겨졌다면 앱이 실행된 것으로 간주
        checkAppLaunched();
      }
    };
    
    // window focus 이벤트 핸들러
    const onWindowFocus = () => {
      // 다른 앱에서 돌아왔다면 앱이 실행된 것으로 간주
      checkAppLaunched();
    };
    
    // 이벤트 리스너 등록
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onWindowFocus);
    
    // 앱 실행 시도
    window.location.href = deeplink;
    
    // 1초 후 앱이 실행되지 않았으면 설치 팝업 표시
    timeoutId = setTimeout(() => {
      if (!appLaunched) {
        // 이벤트 리스너 제거
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('focus', onWindowFocus);
        isProcessing.value = false;
        showDesktopAppInstallPopup();
      }
    }, 1000);
  }

  const showDesktopAppInstallPopup = (isUpdate = false) => {
    dialogStore.open({
      type: DialogType.DesktopAppInstallPopup,
      props: {
        isUpdate
      }
    });
  }

  return {
    isCodepressoWebview: isCodepressoWebview.value,
    isProcessing: isProcessing.value,
    isMac: isMac.value,
    checkVersion,
    getEnvCheckBridge,
    initDeviceSnapshot,
    devicechangeHandler,
    recordingErrorHandler,
    openExternalDirectorApp,
    startCertify,
    finishCertify,
    updateTimer
  };
};